// Client portal sign-in. Three credentials, one door: the email and
// password the invitation sets up, the mailed one-time code for the client
// who never chose a password (when the firm turns that on), and the case
// ID and date of birth the office handed out before accounts existed -
// offered until the firm closes that door in Settings. Plain
// language, no case-management jargon: the person reading this screen is
// a client, not a paralegal.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPortalAuthConfig,
  loginClient,
  loginClientWithCode,
  loginClientWithEmail,
  requestEmailCode,
  requestLoginCode,
  requestPasswordLink,
  verifyLoginCode,
} from "../../api/clientPortal";
import { PortalAuthBrand } from "../../components/portal/PortalAuthBrand";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";

// "twoStep" holds a password sign-in for its mailed code; "codeRequest" and
// "codeSent" are the passwordless door; "linkSent" is forgot's confirmation.
type Mode = "account" | "caseId" | "forgot" | "linkSent" | "twoStep" | "codeRequest" | "codeSent";

const CODE_ENTRY_MODES: Mode[] = ["twoStep", "codeSent"];
const EMAIL_MODES: Mode[] = ["account", "forgot", "codeRequest"];

function submitLabel(mode: Mode, submitting: boolean): string {
  if (mode === "forgot") return submitting ? "Sending..." : "Send me a link";
  if (mode === "codeRequest") return submitting ? "Sending..." : "Email me a code";
  return submitting ? "Signing in..." : "Sign in";
}

function heading(mode: Mode): { h1: string; p: string } {
  switch (mode) {
    // "Set" rather than "reset": most of the people who need this have
    // never had a password, only a case ID.
    case "forgot":
    case "linkSent":
      return {
        h1: "Set a new password",
        p: "Tell us the email address our office has for you. If we have an account for it, we will send a link.",
      };
    case "codeRequest":
      return {
        h1: "Sign in with a code",
        p: "Tell us the email address our office has for you and we will email you a one-time sign-in code.",
      };
    // Neutral about the send: it happens after this screen appears and can
    // fail. The claim that a code is on its way lives in the notice, which
    // only a successful send sets.
    case "twoStep":
      return {
        h1: "One more step",
        p: "Enter the sign-in code from your email to finish signing in.",
      };
    // Says nothing about whether the address is on file - the server takes
    // the same care, and this screen must not undo it.
    case "codeSent":
      return {
        h1: "Check your email",
        p: "If that address is on file with us, a code is on its way. Enter it below.",
      };
    default:
      return {
        h1: "Your case",
        p: "Sign in to see where your case stands and share documents with your legal team.",
      };
  }
}

// "or never set" is the case that actually applies to a client who has only
// ever had a case ID: a plain "forgot your password" invites nobody.
function altsFor(
  mode: Mode,
  otpLogin: boolean,
  caseIdDoor: boolean,
): Array<{ label: string; to: Mode }> {
  const codeDoor = otpLogin
    ? [{ label: "Email me a sign-in code instead", to: "codeRequest" as Mode }]
    : [];
  switch (mode) {
    case "account":
      return [
        { label: "Forgot or never set your password?", to: "forgot" },
        ...codeDoor,
        ...(caseIdDoor ? [{ label: "Use your case ID instead", to: "caseId" as Mode }] : []),
      ];
    case "caseId":
      return [{ label: "Use your email and password instead", to: "account" }, ...codeDoor];
    case "twoStep":
      return [{ label: "Start over", to: "account" }];
    default:
      return [{ label: "Back to sign in", to: "account" }];
  }
}

export default function PortalLogin() {
  const navigate = useNavigate();
  // The case ID and date of birth first: every live matter was given that
  // pair years before accounts existed, and an account only exists once an
  // invitation has gone out. A firm through inviting closes that door in
  // Settings, and then the account door is the first one.
  const [chosen, setMode] = useState<Mode>("caseId");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [eId, setEId] = useState("");
  const [dob, setDob] = useState("");
  const [code, setCode] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const firstField = useRef<HTMLInputElement>(null);
  const note = useRef<HTMLParagraphElement>(null);

  // Through the query cache, not a bare effect: reloads and revisits reuse
  // the answer instead of spending the login rate bucket on every mount.
  // One answer per visit: a refetch that failed would put the fallback's
  // open door under a client mid-form.
  const { data: authConfig } = useQuery({
    queryKey: ["portal", "auth-config"],
    queryFn: fetchPortalAuthConfig,
    staleTime: Infinity,
  });
  const otpLogin = authConfig?.otpLogin === true;
  const caseIdDoor = authConfig?.caseIdLogin !== false;
  const mode: Mode = chosen === "caseId" && !caseIdDoor ? "account" : chosen;
  // The form waits for the answer: a door that appears and then vanishes
  // under a client's fingers is worse than a moment of skeleton.
  const ready = authConfig !== undefined;

  // Switching modes unmounts the control that was just activated, and the
  // confirmation replaces the whole form: without this, focus falls to the
  // document and a keyboard client starts the screen over.
  useEffect(() => {
    (mode === "linkSent" ? note.current : firstField.current)?.focus();
  }, [mode, ready]);

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setCode("");
  }

  async function resendCode() {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    const asked =
      mode === "twoStep"
        ? await requestLoginCode(pendingToken)
        : await requestEmailCode(email.trim());
    setSubmitting(false);
    // The passwordless confirmation hedges like the heading above it: the
    // server answers every address alike, and this screen must not undo it.
    if (asked.ok) {
      setNotice(
        mode === "twoStep"
          ? "A fresh code is on its way."
          : "If that address is on file, a fresh code is on its way.",
      );
    } else {
      setError(asked.error);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);

    if (mode === "forgot") {
      const asked = await requestPasswordLink(email.trim());
      setSubmitting(false);
      if (asked.ok) setMode("linkSent");
      else setError(asked.error);
      return;
    }

    if (mode === "codeRequest") {
      const asked = await requestEmailCode(email.trim());
      setSubmitting(false);
      if (asked.ok) {
        setCode("");
        setMode("codeSent");
      } else {
        setError(asked.error);
      }
      return;
    }

    if (mode === "account") {
      const result = await loginClientWithEmail(email.trim(), password);
      if (result.ok === true) {
        navigate("/portal", { replace: true });
        return;
      }
      // Right password, code still owed: ask for the send right away so the
      // client is reading their mailbox, not hunting for a button.
      if (result.ok === "code") {
        setPendingToken(result.pendingToken);
        setCode("");
        setMode("twoStep");
        const sent = await requestLoginCode(result.pendingToken);
        setSubmitting(false);
        if (sent.ok) setNotice("A code is on its way to your email.");
        else setError(sent.error);
        return;
      }
      setSubmitting(false);
      setError(result.error);
      return;
    }

    const result =
      mode === "twoStep"
        ? await verifyLoginCode(pendingToken, code.trim())
        : mode === "codeSent"
          ? await loginClientWithCode(email.trim(), code.trim())
          : await loginClient(eId.trim(), dob.trim());
    if (result.ok) {
      navigate("/portal", { replace: true });
      return;
    }
    setSubmitting(false);
    setError(result.error);
  }

  if (!ready) {
    return (
      <main className="portal-auth">
        <div className="portal-auth-card" aria-busy="true">
          <div className="portal-auth-head">
            <PortalAuthBrand />
          </div>
          <SkeletonGroup>
            <Skeleton width="55%" height={28} />
            <Skeleton width="90%" />
            <Skeleton height={44} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  const head = heading(mode);

  return (
    <main className="portal-auth">
      <form className="portal-auth-card" onSubmit={onSubmit}>
        <div className="portal-auth-head">
          <PortalAuthBrand />
          <h1>{head.h1}</h1>
          <p>{head.p}</p>
        </div>

        {error && (
          <div className="portal-auth-error" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <p className="portal-auth-note" role="status">
            {notice}
          </p>
        )}

        {mode === "linkSent" ? (
          <>
            {/* Says nothing about whether that address is on file - the
                server takes the same care, and this screen must not undo
                it by answering differently for a client. */}
            <p className="portal-auth-note" role="status" tabIndex={-1} ref={note}>
              If that address is on file with us, a link is on its way. It works once and expires
              after a day.
            </p>
            <div className="portal-auth-alts">
              <button type="button" className="portal-link-btn" onClick={() => switchTo("account")}>
                Back to sign in
              </button>
            </div>
          </>
        ) : (
          <>
            {EMAIL_MODES.includes(mode) && (
              <label className="portal-field">
                <span>Email</span>
                {/* aria-label rather than the wrapping label alone: the hint
                    below would otherwise be read as part of the field name. */}
                <input
                  ref={firstField}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email"
                  autoComplete="email"
                  required
                />
                <small>The address our office has for you.</small>
              </label>
            )}

            {mode === "account" && (
              <label className="portal-field">
                <span>Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
            )}

            {CODE_ENTRY_MODES.includes(mode) && (
              <label className="portal-field">
                <span>Sign-in code</span>
                <input
                  ref={firstField}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  aria-label="Sign-in code"
                  autoComplete="one-time-code"
                  required
                />
                <small>
                  {mode === "twoStep" ? "Six digits, from the email we sent you." : "Six digits."}
                </small>
              </label>
            )}

            {mode === "caseId" && (
              <>
                <label className="portal-field">
                  <span>Case ID</span>
                  <input
                    ref={firstField}
                    className="mono"
                    value={eId}
                    onChange={(e) => setEId(e.target.value)}
                    placeholder="DL-XXXXXX"
                    aria-label="Case ID"
                    autoComplete="off"
                    required
                  />
                  <small>The ID our office gave you. It starts with DL-.</small>
                </label>

                <label className="portal-field">
                  <span>Date of birth</span>
                  {/* A native date field: it submits the ISO string the server
                      parses first, and a phone keypad cannot type hyphens. */}
                  <input
                    type="date"
                    className="tnum"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    aria-label="Date of birth"
                    required
                  />
                  <small>The date of birth our office has on file for you.</small>
                </label>
              </>
            )}

            <button type="submit" className="portal-auth-submit" disabled={submitting}>
              {submitLabel(mode, submitting)}
            </button>

            <div className="portal-auth-alts">
              {CODE_ENTRY_MODES.includes(mode) && (
                <button
                  type="button"
                  className="portal-link-btn"
                  disabled={submitting}
                  onClick={() => void resendCode()}
                >
                  Send a new code
                </button>
              )}
              {altsFor(mode, otpLogin, caseIdDoor).map((alt) => (
                // Disabled mid-request: a switch while a sign-in is in
                // flight lands its error, its spinner and its confirmation
                // on a form the client never submitted.
                <button
                  key={alt.label}
                  type="button"
                  className="portal-link-btn"
                  disabled={submitting}
                  onClick={() => switchTo(alt.to)}
                >
                  {alt.label}
                </button>
              ))}
            </div>
          </>
        )}

        <p className="portal-auth-foot">
          Trouble signing in? Call our office and we will help. Do not share your sign-in details
          with anyone.
        </p>
      </form>
    </main>
  );
}
